import React, { CSSProperties } from 'react';
import debounce from 'lodash.debounce';
import {
  getTransformOriginByPosition,
  Position
} from 'common/js/creative-position';
import eventBus from 'common/event-handling/event-bus.model';
import { PreloadedEvent } from 'common/event-handling/events';
import { isAndroid, isWeb } from '../../js/environment-utils';
import { RenderConfig } from '../../../legacy/common/js/AdParser';

interface Props {
  srcDoc: string;
  onClick?: (e: ClickEvent) => void;
  shouldRefreshFrameOnRotation: boolean;
  forceResponsiveHtml: boolean;
  position?: Position;
  onIframeResized?: ({
    boundingClientRect
  }: {
    boundingClientRect: DOMRect;
  }) => void;
  disableUseCustomClose?: boolean;
  injectMRAID?: boolean;
  supportMRAIDExpand?: boolean;

  renderConfig?: RenderConfig;
  backendWidth?: number | null;
  backendHeight?: number | null;
}

interface State {
  isLoadingHtml: boolean;
  isInjected: boolean;
  style: CSSProperties;
  scale: number;
}

export class Iframe extends React.Component<Props, State> {
  private readonly iframeRef: React.RefObject<HTMLIFrameElement>;
  private WIDTH: number = 50;
  private HEIGHT: number = 50;

  // Default responsive HTML style (for creative using responsive layout)
  private readonly FORCE_RESPONSIVE_HTML_STYLE: CSSProperties = {
    flexShrink: 0,
    height: '100%',
    width: '100%',
    visibility: 'visible',
    zIndex: 1
  };

  constructor(props: Props) {
    super(props);
    this.iframeRef = React.createRef<HTMLIFrameElement>();

    const isResponsive =
      props.renderConfig?.isResponsive || props.forceResponsiveHtml;

    // Initial style depends on responsive mode
    let style: CSSProperties = {};
    if (isResponsive) {
      style = this.FORCE_RESPONSIVE_HTML_STYLE;
    } else {
      // Static size by default (hidden until measured)
      style = {
        flexShrink: 0,
        height: `${this.HEIGHT}px`,
        width: `${this.WIDTH}px`,
        visibility: 'hidden',
        zIndex: 1
      };
    }

    this.state = {
      isLoadingHtml: false,
      isInjected: false,
      style,
      scale: 0
    };

    // Bind methods + debounce resize
    this.resizeIframe = debounce(this.resizeIframe.bind(this), 50);
    this.scaleBasedOnWindow = this.scaleBasedOnWindow.bind(this);
    this.injectDOM = this.injectDOM.bind(this);
    this.onLoad = this.onLoad.bind(this);
    this.getParentSize = this.getParentSize.bind(this);
    this.checkInterval = this.checkInterval.bind(this);
    this.isOguryWrapperFormat = this.isOguryWrapperFormat.bind(this);

    // Handle click events forwarded from inside iframe
    let alreadySentClick = false;

    const messageListener = (event: any) => {
      if (event.data === 'clickFromIframe' && !alreadySentClick) {
        alreadySentClick = true;
        window.removeEventListener('message', messageListener, false);

        // Use optional chaining to avoid crash if undefined
        props.onClick?.((event as unknown) as any);
      }
    };

    window.addEventListener('message', messageListener, false);
  }

  shouldComponentUpdate(nextProps: Readonly<Props>, nextState: any): boolean {
    // Prevent unnecessary re-renders: only update if dimensions or srcDoc change
    return (
      nextProps.srcDoc !== this.props.srcDoc ||
      nextProps.backendWidth !== this.props.backendWidth ||
      nextProps.renderConfig?.isResponsive !==
        this.props.renderConfig?.isResponsive ||
      nextState.style.width !== this.state.style.width ||
      nextState.style.height !== this.state.style.height ||
      nextState.style.transform !== this.state.style.transform
    );
  }

  componentDidMount() {
    this.injectDOM();
    this.resizeIframe();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    this.injectDOM();

    // Recalculate size when creative or backend dimensions change
    if (
      prevProps.srcDoc !== this.props.srcDoc ||
      prevProps.backendWidth !== this.props.backendWidth
    ) {
      this.resizeIframe();
    }
  }

  injectDOM() {
    const html = this.props.srcDoc;

    // Inject HTML only once
    if (
      html &&
      !this.state.isInjected &&
      this.iframeRef.current?.contentWindow
    ) {
      this.setState({ isInjected: true });
      const windowIframe = this.iframeRef.current.contentWindow;

      // Inject MRAID bridge if required
      if (
        this.props.injectMRAID === true ||
        this.props.injectMRAID === undefined
      ) {
        this.injectMraidIntoIframe(
          this.props.disableUseCustomClose,
          this.props.supportMRAIDExpand,
          windowIframe
        );
      }

      // Write the HTML doc into the iframe
      windowIframe.document.open();
      windowIframe.document.write(html);
      windowIframe.document.close();
    }
  }

  injectMraidIntoIframe(
    disableUseCustomClose: boolean | undefined,
    supportMRAIDExpand: boolean | undefined,
    windowIframe: Window
  ) {
    // Inject the host MRAID into the iframe
    const hostMraid = (window as Window).mraid;
    if (!hostMraid) return;

    // @ts-ignore
    windowIframe.mraid = hostMraid;

    // Disable expand if required
    if (!supportMRAIDExpand) {
      // @ts-ignore
      windowIframe.mraid.expand = () => {};
    }

    // Disable custom close if required
    if (disableUseCustomClose) {
      // @ts-ignore
      windowIframe.mraid.useCustomClose = () => {};
    }
  }

  // Compute a scaling factor so the creative fits in its container
  scaleBasedOnWindow(
    elm: HTMLIFrameElement | null,
    elmWidth: number,
    elmHeight: number,
    innerWidth: number,
    innerHeight: number
  ): number {
    if (!elm) return 1;
    if (innerWidth === 0 || innerHeight === 0) return 1;

    return 1 / Math.max(elmWidth / innerWidth, elmHeight / innerHeight);
  }

  // Detect Ogury MRAID-wrapper creative (nested iframe inside the creative)
  // For creative wrapper in Ogury way (MPU Pharma), check if the iframe is present and take the size from here
  // Because the size of the body is not well set by the Ogury Wrapper.
  isOguryWrapperFormat(iframe: HTMLIFrameElement) {
    const OguryAdIframe = (iframe.contentWindow?.document.getElementById(
      'Ad'
    ) as unknown) as HTMLIFrameElement;

    if (
      OguryAdIframe &&
      OguryAdIframe.tagName === 'IFRAME' &&
      OguryAdIframe.src &&
      OguryAdIframe.src.indexOf('medias.presage') > -1
    ) {
      return OguryAdIframe;
    }
    return false;
  }

  resizeIframe() {
    const {
      width: containerWidth,
      height: containerHeight
    } = this.getParentSize();

    const iframe = this.iframeRef.current;
    if (!iframe) return;

    const {
      renderConfig,
      backendWidth,
      backendHeight,
      forceResponsiveHtml
    } = this.props;

    //
    // 1 — Fully responsive creative: just stretch to container
    //
    if (renderConfig?.isResponsive || forceResponsiveHtml) {
      this.setState({ style: this.FORCE_RESPONSIVE_HTML_STYLE });
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.visibility = 'visible';
      this.notifyResize(iframe);
      return;
    }

    //
    // 2 — Creative has known backend dimensions (ideal case)
    //
    const creativeWidth = backendWidth || 0;
    const creativeHeight = backendHeight || 0;

    if (creativeWidth > 1 && creativeHeight > 1) {
      const scale = this.scaleBasedOnWindow(
        iframe,
        creativeWidth,
        creativeHeight,
        containerWidth,
        containerHeight
      );

      this.applyFixedStyle(creativeWidth, creativeHeight, scale);
      this.notifyResize(iframe);
      return;
    }

    //
    // 3 — Fallback: measure dimensions via polling (scrollWidth/scrollHeight)
    //
    iframe.style.width = `${this.WIDTH}px`;
    iframe.style.height = `${this.HEIGHT}px`;

    iframe.width = String(this.WIDTH);
    iframe.height = String(this.HEIGHT);

    let scrollHeight = iframe.contentWindow?.document.body?.scrollHeight || 0;
    let scrollWidth = iframe.contentWindow?.document.body?.scrollWidth || 0;

    //
    // Special case: Ogury MRAID wrapper with nested iframe (#Ad)
    //
    const oguryWrapper = this.isOguryWrapperFormat(iframe);
    if (oguryWrapper) {
      scrollWidth =
        Number.parseInt(oguryWrapper.style.width || '0', 10) ||
        oguryWrapper.scrollWidth;

      scrollHeight =
        Number.parseInt(oguryWrapper.style.height || '0', 10) ||
        oguryWrapper.scrollHeight;
    }
    // If the width is equal to the iframe size (50px), it means the creative is:
    //  - fully responsive
    //  - don't have the time to load, so we let it load and check on a next interval (we let the script in a 100% view to avoid issues
    // If the height is equal to the iframe size (50px), it can be a small banner creative
    // So we decide not pass in this condition as we will need to scale it
    // In case of Celtra:
    //    - they put a min-height on their creatives that leads to a > height
    //    - they don't put a min width so the size is equal to the iframe size
    if (this.WIDTH === scrollWidth) {
      this.setState({
        style: {
          ...this.state.style,
          width: containerWidth + 'px',
          height: containerHeight + 'px',
          visibility: 'visible',
          transform: 'none'
        },
        scale: 1
      });

      iframe.style.width = containerWidth + 'px';
      iframe.style.height = containerHeight + 'px';
    } else {
      // Otherwise scale based on detected creative size
      const scale = this.scaleBasedOnWindow(
        iframe,
        scrollWidth,
        scrollHeight,
        containerWidth,
        containerHeight
      );
      this.applyFixedStyle(scrollWidth, scrollHeight, scale);
    }

    iframe.style.visibility = 'visible';
    this.notifyResize(iframe);
  }

  // Apply fixed dimensions + scaling
  applyFixedStyle(w: number, h: number, scale: number) {
    const iframe = this.iframeRef.current;
    if (!iframe) return;

    this.setState({
      style: {
        ...this.state.style,
        width: w + 'px',
        height: h + 'px',
        visibility: 'visible',
        zIndex: 1,
        transform: `scale(${scale})`,
        transformOrigin: getTransformOriginByPosition(this.props.position)
      },
      scale: scale || 0
    });

    iframe.style.width = w + 'px';
    iframe.style.height = h + 'px';
    iframe.style.visibility = 'visible';
  }

  // Return parent container size or fallback to window size
  getParentSize() {
    if (this.iframeRef && this.iframeRef.current) {
      const parent = this.iframeRef.current.parentElement;
      const w = parent?.clientWidth || 0;
      const h = parent?.clientHeight || 0;
      if (w > 0 && h > 0) return { width: w, height: h };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }

  // Notify parent component of new iframe bounding box
  notifyResize(iframe: HTMLIFrameElement) {
    if (this.props.onIframeResized) {
      this.props.onIframeResized({
        boundingClientRect: iframe.getBoundingClientRect()
      });
    }
  }

  onLoad() {
    if (!this.props.srcDoc) return;

    // Signal that creative HTML is preloaded
    eventBus.publish(new PreloadedEvent());

    this.resizeIframe();
    this.checkInterval();

    // Listen for custom "adinitialized" event from inside iframe
    if (this.iframeRef && this.iframeRef.current) {
      this.iframeRef.current.contentWindow?.addEventListener(
        'adinitialized',
        () => {
          // For GWD HTML, we have a long event before the creative is rendered, so avoid reload it
          this.setState({ isLoadingHtml: true });
          // SetTimeout because the ad init will be trigger first without the right size
          setTimeout(this.resizeIframe, 50);
        }
      );
    }

    // Handle resize (including rotation on mobile)
    window.addEventListener('resize', e => {
      // Reload the content of the iframe
      // In some HTML, they calculate the size of the webview at start
      // and they don't recalculate it after the resize
      // That leads to some bad responsive design
      if (
        this.props.shouldRefreshFrameOnRotation &&
        this.iframeRef.current &&
        !this.state.isLoadingHtml
      ) {
        this.iframeRef.current.srcdoc = this.props.srcDoc;
      }

      // To avoid the hotfix done for mraid on the mraid store
      // @ts-ignore
      if (!e || !e.detail || !e.detail.isManual) {
        setTimeout(this.resizeIframe, 50);
      }
    });
  }

  // Progressive polling algorithm to continuously detect creative size
  checkInterval(interval = 50) {
    const resize = () => {
      // Increment the interval to avoid doing it too much time
      // And do it more at the initialization than at the end
      // It will allow us to detect some edges cases where the creative is loaded after a certain time and we don't have a specific events
      // Example: https://cdn.jsdelivr.net/gh/Themandunord/html-files/japan/Lenovo/Security-Messaging-C/300x600/RTTC_Security-Lenovo_JPN_300x600_BAN_NA_EN_NA_Standard_ANI_LEA_NA_1/300x600.html
      interval *= 2;
      this.resizeIframe();
      if (interval < 3000) this.checkInterval(interval);
    };
    setTimeout(resize, interval);

    // Workaround for Android SDK ADV-16511
    // Some OX creative ask for mraid.js, the Android SDK answer with the cache
    // But the Bunazia call reset the viewability of the ad
    // This fix allows the SDK to check again the viewability of the webview
    if (isAndroid() && !isWeb()) {
      // @ts-ignore
      window.mraid?.ogyUpdateWebView?.('controller');
    }
  }

  render() {
    const { style } = this.state;

    return (
      <iframe
        srcDoc=""
        ref={this.iframeRef}
        onLoad={this.onLoad}
        style={style}
        frameBorder={'0'}
        marginWidth={0}
        marginHeight={0}
        scrolling="no"
      />
    );
  }
}
